import { useBlocker } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * AB#430: blocks in-app navigation (and warns on tab close) while a form is dirty.
 *
 * Drop `<UnsavedGuard dirty={dirty} noun="post" />` inside an editor. When `dirty`
 * and the user navigates away, TanStack Router's blocker resolves to `blocked` and
 * we show a Stay/Discard dialog. `enableBeforeUnload` gives the native prompt on a
 * real tab/window close for free. Clean (dirty=false) never blocks → no prompt.
 */
export function UnsavedGuard({ dirty, noun = 'page' }: { dirty: boolean; noun?: string }) {
  const { status, proceed, reset } = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  return (
    <Dialog
      open={status === 'blocked'}
      onOpenChange={(open) => {
        if (!open) reset?.();
      }}
    >
      <DialogContent showCloseButton={false} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Discard unsaved changes?</DialogTitle>
          <DialogDescription>
            You have unsaved edits on this {noun}. If you leave now they will be lost.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => reset?.()}>
            Stay
          </Button>
          <Button variant="destructive" onClick={() => proceed?.()}>
            Discard changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
